import { useState } from 'react';
import { Button, Space, message } from 'antd';
import { DownloadOutlined, ProfileOutlined } from '@ant-design/icons';
import type { RegistryRecord } from '../../api/registryRecordApi';
import type { Patient } from '../../api/patientApi';
import RegistryQuestionnaireView, { type RegistryQuestionnaireProgram } from './RegistryQuestionnaireView';
import PhaseShell, { SectionCard, StatChip } from './PhaseShell';
import { SectionEmpty, SourceDocLine } from './registryDocGroups';
import { downloadBlob } from '../../pages/dermato/medicalDocuments';
import type { PhaseStatus } from './RegistryPhaseWorkspace';

const countSections = (record: RegistryRecord): number =>
  Object.values(record.questionnaire ?? {}).filter(
    (s) => s && typeof s === 'object' && !Array.isArray(s) && Object.keys(s as object).length > 0,
  ).length;

interface Props {
  /** ex. „Faza 1 — Conștientizare & Anamneză” */
  title: string;
  program: RegistryQuestionnaireProgram;
  record: RegistryRecord;
  patient: Patient | null;
  status: PhaseStatus;
  downloaders: {
    anamnesis: (formId: string) => Promise<Blob>;
    full: (formId: string) => Promise<Blob>;
  };
}

/** Faza 1 (comună hepato/dermato): chestionarul importat + exporturile PDF. */
export default function AnamnesisPhasePage({ title, program, record, status, downloaders }: Props) {
  const [pdfLoading, setPdfLoading] = useState<'anamnesis' | 'full' | null>(null);
  const chestionar = record.documents.find((d) => d.kind === 'QUESTIONNAIRE') ?? null;
  const sections = countSections(record);

  const download = async (kind: 'anamnesis' | 'full') => {
    if (!record.formId) return;
    setPdfLoading(kind);
    try {
      const blob =
        kind === 'anamnesis' ? await downloaders.anamnesis(record.formId) : await downloaders.full(record.formId);
      const dateStr = new Date().toISOString().split('T')[0];
      downloadBlob(
        blob,
        `HEPASKIN_${program === 'dermato' ? 'Dermato' : 'Hepato'}_${kind === 'full' ? 'Complet' : 'Chestionar'}_${dateStr}.pdf`,
      );
      message.success('PDF descărcat');
    } catch {
      message.error('Eroare la descărcarea PDF-ului');
    } finally {
      setPdfLoading(null);
    }
  };

  return (
    <PhaseShell
      title={title}
      description="Trierea pacientului prin chestionarul de anamneză și evaluarea factorilor de risc."
      status={status}
      chips={<StatChip count={sections} label="secțiuni completate" />}
      extra={
        record.formId ? (
          <Space wrap>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={pdfLoading === 'anamnesis'}
              onClick={() => void download('anamnesis')}
            >
              Chestionar PDF
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={pdfLoading === 'full'}
              onClick={() => void download('full')}
            >
              Chestionar complet PDF
            </Button>
          </Space>
        ) : null
      }
    >
      <SectionCard icon={<ProfileOutlined />} title="Chestionarul de anamneză">
        <SourceDocLine document={chestionar} />
        {sections > 0 || record.consent ? (
          <RegistryQuestionnaireView program={program} sections={record.questionnaire} consent={record.consent} />
        ) : (
          <SectionEmpty />
        )}
      </SectionCard>
    </PhaseShell>
  );
}
